import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const AdminHeader = ({ adminData, onLogout, currentView }) => {
  const navigate = useNavigate()
  const [isProfileOpen, setIsProfileOpen] = useState(false)

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo and title */}
          <div className="flex items-center">
            <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                CEDEC Admin
              </h1>
              <p className="text-sm text-gray-500">
                Panneau d'administration
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex space-x-8">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                currentView === 'dashboard'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              Tableau de bord
            </button>
            <button
              onClick={() => navigate('/admin/assessments')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                currentView === 'assessments' || currentView === 'detail'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              Évaluations
            </button>
            {(adminData?.role === 'editor' || adminData?.role === 'admin') && (
              <button
                onClick={() => navigate('/admin/questionnaire')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  currentView === 'questionnaire'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Questionnaire
              </button>
            )}
            {adminData?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin/users')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  currentView === 'users'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Utilisateurs
              </button>
            )}
            {adminData?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin/tests')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  currentView === 'tests'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Tests & Utilitaires
              </button>
            )}
          </nav>

          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center text-sm bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {adminData?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="ml-3 text-gray-700 font-medium hidden sm:block">
                {adminData?.email}
              </span>
              <svg className="ml-2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm text-gray-500">Connecté en tant que</p>
                  <p className="text-sm font-medium text-gray-900">{adminData?.email}</p>
                  {adminData?.role && (
                    <p className="text-xs text-gray-600 mt-1">
                      Rôle: <span className="font-medium capitalize">{adminData.role}</span>
                    </p>
                  )}
                  {adminData?.loginAt && (
                    <p className="text-xs text-gray-500 mt-1">
                      Connexion: {formatDate(adminData.loginAt)}
                    </p>
                  )}
                </div>
                <button
                  onClick={onLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Se déconnecter
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      <div className="md:hidden border-t border-gray-200">
        <nav className="flex overflow-x-auto">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className={`flex-1 py-3 text-center text-sm font-medium transition-colors whitespace-nowrap ${
              currentView === 'dashboard'
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => navigate('/admin/assessments')}
            className={`flex-1 py-3 text-center text-sm font-medium transition-colors whitespace-nowrap ${
              currentView === 'assessments' || currentView === 'detail'
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Évaluations
          </button>
          {(adminData?.role === 'editor' || adminData?.role === 'admin') && (
            <button
              onClick={() => navigate('/admin/questionnaire')}
              className={`flex-1 py-3 text-center text-sm font-medium transition-colors whitespace-nowrap ${
                currentView === 'questionnaire'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Questionnaire
            </button>
          )}
          {adminData?.role === 'admin' && (
            <button
              onClick={() => navigate('/admin/users')}
              className={`flex-1 py-3 text-center text-sm font-medium transition-colors whitespace-nowrap ${
                currentView === 'users'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Utilisateurs
            </button>
          )}
          {adminData?.role === 'admin' && (
            <button
              onClick={() => navigate('/admin/tests')}
              className={`flex-1 py-3 text-center text-sm font-medium transition-colors whitespace-nowrap ${
                currentView === 'tests'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Tests
            </button>
          )}
        </nav>
      </div>
    </header>
  )
}

export default AdminHeader
